import ScheduleIcon from "@mui/icons-material/Schedule";
import { Box, Stack, Typography } from "@mui/material";
import { CreditStatus, DebtType } from "../../Models/Credit";
import { DANGER, INK, TEXT_MUTED } from "../../Utils/designTokens";
import { getRemoteText, getRemoteValue } from "../../Utils/firebase";
import CLoadingButton from "../CLoadingButton/CLoadingButton";
import CModal from "../CModal/CModal";
import { FormatAmountSymbol } from "../FormatData/FormatAmount";
import FormatDateToTR from "../FormatData/FormatDate";

interface Props {
  open: boolean;
  onClose: () => void;
  debt: DebtType;
  /** Pay CTA; the card's existing pay flow. */
  onPay: () => void;
  /** Snooze CTA; only called when the debt is snoozable. */
  onSnooze: () => void;
}

/**
 * CRL-3241: pending payment detail modal, opened by pressing a card on the
 * pending payments tab. The mock's "Bu harcamaya itiraz et" link is
 * intentionally absent (no dispute logic).
 */
const PendingDebtDetailModal = ({ open, onClose, debt, onPay, onSnooze }: Props) => {
  if (!open) return null;

  const isLate = debt.status === CreditStatus.LATE;
  // No snooze-count field exists on the plan; an accrued snooze fee is the
  // trace a past postponement leaves, so it picks the "snoozed before" copy.
  const wasSnoozedBefore = debt.snoozeAmount > 0;

  return (
    <CModal
      open={open}
      onClose={onClose}
      contentSx={{
        "@media (min-width: 768.5px)": {
          width: "min(480px, 92vw)",
          borderRadius: "24px",
        },
      }}
    >
      <Box>
        {/* Header: shop + amount · shopping date */}
        <Stack direction={"row"} alignItems={"center"} gap={"14px"} pr={5}>
          <img
            src={debt.shop.logoUrl}
            alt={debt.shop.name}
            style={{
              objectFit: "cover",
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              flexShrink: 0,
            }}
          />
          <Stack sx={{ minWidth: 0 }}>
            <Typography
              lineHeight={"normal"}
              fontSize={"18px"}
              fontWeight={700}
              color={INK}
              noWrap
            >
              {debt.shop.name}
            </Typography>
            <Typography
              lineHeight={"normal"}
              fontSize={"14px"}
              color={TEXT_MUTED}
              pt={"4px"}
            >
              {FormatAmountSymbol(debt.creditAmount)} ·{" "}
              {FormatDateToTR(debt.createdAt)}
            </Typography>
          </Stack>
        </Stack>

        {/* Son Ödeme Tarihi | İşlem No */}
        <Stack direction={"row"} pt={3} gap={2}>
          <Stack sx={{ flex: 1 }}>
            <Typography fontSize={"13px"} color={TEXT_MUTED} lineHeight={"normal"}>
              {getRemoteValue("last_payment_date")}
            </Typography>
            <Typography
              fontSize={"17px"}
              fontWeight={700}
              color={INK}
              lineHeight={"normal"}
              pt={"6px"}
            >
              {FormatDateToTR(debt.dueDate)}
            </Typography>
          </Stack>
          <Stack sx={{ flex: 1 }}>
            <Typography fontSize={"13px"} color={TEXT_MUTED} lineHeight={"normal"}>
              {getRemoteValue("transaction_number")}
            </Typography>
            <Typography
              fontSize={"17px"}
              fontWeight={700}
              color={INK}
              lineHeight={"normal"}
              pt={"6px"}
            >
              {debt.creditId}
            </Typography>
          </Stack>
        </Stack>

        {/* Ödeme Tutarı */}
        <Stack pt={3}>
          <Typography fontSize={"13px"} color={TEXT_MUTED} lineHeight={"normal"}>
            {getRemoteText("payment_amount_label", "Ödeme Tutarı")}
          </Typography>
          <Typography
            fontSize={"26px"}
            fontWeight={800}
            color={isLate ? DANGER : INK}
            lineHeight={"normal"}
            pt={"6px"}
          >
            {FormatAmountSymbol(debt.remainingPaymentAmount)}
          </Typography>
        </Stack>

        {/* Not-snoozable notice */}
        {!debt.snoozeAvailable && (
          <Stack
            direction={"row"}
            gap={"10px"}
            sx={{
              backgroundColor: "#FFF8E6",
              border: "1px solid #F5E6C0",
              borderRadius: "14px",
              padding: "14px 16px",
              mt: 3,
            }}
          >
            <ScheduleIcon sx={{ fontSize: 18, color: "#B7791F", mt: "1px", flexShrink: 0 }} />
            <Typography fontSize={"13px"} color={"#92610E"} lineHeight={1.5}>
              <b>{getRemoteText("not_snoozable_title", "Bu harcama ertelenemez.")}</b>
              {wasSnoozedBefore && (
                <>
                  {" "}
                  {getRemoteText(
                    "not_snoozable_snoozed_desc",
                    "Bu harcama daha önce 1 kez ertelendi, tekrar ertelenemez."
                  )}
                </>
              )}
            </Typography>
          </Stack>
        )}

        {/* Actions */}
        <Stack direction={"row"} gap={"14px"} pt={3}>
          <CLoadingButton
            fullWidth
            size="large"
            variant="contained"
            disabled={!debt.snoozeAvailable}
            sx={{
              flex: 1,
              backgroundColor: "#F1F5F9",
              color: INK,
              "&:hover": { backgroundColor: "#E2E8F0" },
            }}
            onClick={onSnooze}
          >
            {getRemoteValue("snooze")}
          </CLoadingButton>
          <CLoadingButton
            fullWidth
            size="large"
            variant="contained"
            sx={{ flex: 1 }}
            onClick={onPay}
          >
            {getRemoteValue("pay")}
          </CLoadingButton>
        </Stack>
      </Box>
    </CModal>
  );
};

export default PendingDebtDetailModal;
